import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Database, CheckCircle2 } from 'lucide-react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { useToast } from './ui/Toast';
import { useCan } from '../hooks/useCan';
import {
  useSampleDataStatus,
  useLoadSampleData,
  useRemoveSampleData,
} from '../hooks/useSampleData';

/**
 * Settings card for one-click SAMPLE / PREPARED data (Wave 3). Shows the
 * current state and a Load/Remove control. Loading the dataset lets a new
 * workspace instantly show "what good looks like"; removal is clean and never
 * touches real data. Viewer-gated (read-only for viewers).
 */
export function SampleDataCard() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const toast = useToast();
  const { canMutate } = useCan();
  const { data: status } = useSampleDataStatus(wsId);
  const load = useLoadSampleData(wsId);
  const remove = useRemoveSampleData(wsId);
  const [confirming, setConfirming] = useState(false);

  const present = status?.present ?? false;

  const onLoad = () =>
    load.mutate(undefined, {
      onSuccess: () => toast.success(t('sampleData.loaded')),
      onError: () => toast.error(t('sampleData.error')),
    });

  const onRemove = () => {
    setConfirming(false);
    remove.mutate(undefined, {
      onSuccess: () => toast.success(t('sampleData.removed')),
      onError: () => toast.error(t('sampleData.error')),
    });
  };

  return (
    <Card className="mb-5">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Database size={16} className="text-accent-600 dark:text-accent-300" />
          <div className="font-medium">{t('sampleData.title')}</div>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-ink-500">{t('sampleData.description')}</p>

        {present ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={15} />
            <span>{t('sampleData.present')}</span>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          {!present ? (
            <button
              type="button"
              onClick={onLoad}
              disabled={!canMutate || load.isPending}
              title={canMutate ? undefined : t('common.viewerReadOnly')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-600 text-white text-sm font-medium hover:bg-accent-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              <Database size={14} />
              {load.isPending ? t('sampleData.loading') : t('sampleData.load')}
            </button>
          ) : confirming ? (
            <>
              <span className="text-sm text-ink-600 dark:text-ink-300">
                {t('sampleData.confirmRemove')}
              </span>
              <button
                type="button"
                onClick={onRemove}
                disabled={remove.isPending}
                className="px-3 py-1.5 rounded-lg bg-warn-600 text-white text-xs font-medium hover:opacity-90 disabled:opacity-60"
              >
                {remove.isPending ? t('sampleData.removing') : t('common.confirm')}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium"
              >
                {t('common.cancel')}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={!canMutate || remove.isPending}
              title={canMutate ? undefined : t('common.viewerReadOnly')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 text-sm font-medium hover:bg-ink-100 dark:hover:bg-ink-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {t('sampleData.remove')}
            </button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
